// src/components/common/audio-player/TownLITAudioPlayer.tsx
//
// Created by Hossein Sakkaki on 2026-06-24.
// Last Update by Hossein Sakkaki on 2026-06-24.

"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { usePlaybackState } from "./core/usePlaybackState";
import { useAudioEngine } from "./core/useAudioEngine";
import { useAudioVisualizer } from "./dsp/useAudioVisualizer";
import AudioSeekBar from "./ui/AudioSeekBar";
import AudioControls from "./ui/AudioControls";
import { useRegisterAudioSession } from "./session/useRegisterAudioSession";
import { useAudioSessionControls } from "./session/useAudioSessionControls";
import { useOSMediaSession } from "./session/useOSMediaSession";
import { usePlaybackPlatform } from "@/playback-runtime/platform/usePlaybackPlatform";

import "./TownLITAudioPlayer.css";

export type PlayRate = 0.75 | 1 | 1.25;

const RATES: PlayRate[] = [0.75, 1, 1.25];

const isHls = (url: string) => /\.m3u8(\?.*)?$/i.test(url);

const supportsNativeHls = () => {
  if (typeof document === "undefined") return false;

  const audio = document.createElement("audio");

  return Boolean(
    audio.canPlayType("application/vnd.apple.mpegurl") ||
      audio.canPlayType("audio/mpegurl"),
  );
};

const fmt = (s: number) => {
  if (!isFinite(s) || isNaN(s) || s < 0) return "00:00";

  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60)
    .toString()
    .padStart(2, "0");

  return `${m}:${sec}`;
};

export type TownLITAudioPlayerProps = {
  src?: string;

  /**
   * Asset Delivery playback payload.
   *
   * refresh must use /assets/playback/refresh/ or equivalent
   * target-based refresh. It must not use raw storage keys.
   */
  playback?: {
    url: string;
    refresh: () => Promise<string>;
  };

  title?: string;
  artist?: string;
  artwork?: string;
  autoPlay?: boolean;
  initialRate?: PlayRate;
  forceShaka?: boolean;
  onReady?: () => void;
  onEnded?: () => void;
};

const TownLITAudioPlayer: React.FC<TownLITAudioPlayerProps> = ({
  src,
  playback,
  title,
  artist,
  artwork,
  autoPlay = false,
  initialRate = 1,
  forceShaka = false,
  onReady,
  onEnded,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { state, controls } = usePlaybackState(initialRate);
  const {
    playing,
    duration,
    time,
    bufferedEnd: bufEnd,
    rate,
  } = state;

  const {
    setPlaying,
    setDuration,
    setTime,
    setBufferedEnd: setBufEnd,
    setRate,
  } = controls;

  const [reduceMotion, setReduceMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Prefer resolved Asset Delivery URL.
  const finalSrc = playback?.url ?? src ?? null;
  const resolvedSrc = finalSrc ?? "";

  useOSMediaSession({
    title,
    artist,
    artwork,
  });

  usePlaybackPlatform(audioRef, {
    src: resolvedSrc,
  });

  // Detect mobile once.
  useEffect(() => {
    if (typeof navigator === "undefined") return;

    const ua = navigator.userAgent || "";
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(ua));
  }, []);

  // Respect reduced motion.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");

    const sync = () => {
      setReduceMotion(!!mq?.matches);
    };

    sync();

    mq?.addEventListener?.("change", sync);

    return () => {
      mq?.removeEventListener?.("change", sync);
    };
  }, []);

  const nativeHlsSupported = useMemo(() => supportsNativeHls(), []);

  // Prefer native HLS on mobile.
  const preferNativeHlsOnMobile = useMemo(() => {
    return (
      isMobile &&
      nativeHlsSupported &&
      !!resolvedSrc &&
      isHls(resolvedSrc)
    );
  }, [
    isMobile,
    nativeHlsSupported,
    resolvedSrc,
  ]);

  // Use Shaka only when needed.
  const useShaka = useMemo(() => {
    if (!resolvedSrc) return false;
    if (!isHls(resolvedSrc)) return false;
    if (forceShaka) return true;

    return !preferNativeHlsOnMobile;
  }, [
    resolvedSrc,
    forceShaka,
    preferNativeHlsOnMobile,
  ]);

  const engineControls = useMemo(() => {
    return {
      setPlaying,
      setDuration,
      setTime,
      setBufferedEnd: setBufEnd,
    };
  }, [
    setPlaying,
    setDuration,
    setTime,
    setBufEnd,
  ]);

  const engineOpts = useMemo(() => {
    const hls = resolvedSrc ? isHls(resolvedSrc) : false;

    return {
      src: resolvedSrc || "",
      resolvedSrc,
      useShaka,
      autoPlay,
      onReady,
      onEnded,

      // Refresh Asset Delivery cookies when needed.
      ensureCookies:
        hls && playback?.refresh
          ? async () => {
              await playback.refresh();
            }
          : undefined,
    };
  }, [
    resolvedSrc,
    useShaka,
    autoPlay,
    onReady,
    onEnded,
    playback?.refresh,
  ]);

  useAudioEngine(
    audioRef,
    engineOpts,
    engineControls,
  );

  // Keep visualizer off on mobile.
  useAudioVisualizer(
    audioRef,
    canvasRef,
    {
      playing,
      rate,
      isMobile,
      reduceMotion,
    },
  );

  // Sync rate to element.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    el.playbackRate = rate;
    el.defaultPlaybackRate = rate;
  }, [rate]);

  const ownerId = useRegisterAudioSession(audioRef);
  const session = useAudioSessionControls(audioRef, ownerId);

  const toggle = session.toggle;
  const seek = session.seek;

  const skip = useCallback(
    (delta: number) => {
      const el = audioRef.current;
      if (!el) return;

      session.seek((el.currentTime || 0) + delta);
    },
    [session],
  );

  const slower = useCallback(() => {
    const i = RATES.indexOf(rate);
    const next = RATES[Math.max(0, i - 1)];

    setRate(next);
    session.setRate(next);
  }, [
    rate,
    session,
    setRate,
  ]);

  const faster = useCallback(() => {
    const i = RATES.indexOf(rate);
    const next = RATES[Math.min(RATES.length - 1, i + 1)];

    setRate(next);
    session.setRate(next);
  }, [
    rate,
    session,
    setRate,
  ]);

  const bufferedPct = useMemo(
    () =>
      !duration
        ? 0
        : Math.max(
            0,
            Math.min(100, (bufEnd / duration) * 100),
          ),
    [
      bufEnd,
      duration,
    ],
  );

  const playedPct = useMemo(
    () =>
      !duration
        ? 0
        : Math.max(
            0,
            Math.min(100, (time / duration) * 100),
          ),
    [
      time,
      duration,
    ],
  );

  if (!resolvedSrc) return null;

  return (
    <div
      className="tl-audio compact"
      role="group"
      aria-label="TownLIT audio player"
    >
      <div className="tl-meta-visual-container">
        <div className="tl-meta">
          {artwork && (
            <img
              className="tl-cover"
              src={artwork}
              alt=""
            />
          )}
        </div>

        <div className="tl-visual-wrap">
          <canvas
            className="tl-visual"
            ref={canvasRef}
          />
        </div>
      </div>

      <AudioSeekBar
        time={time}
        duration={duration}
        bufferedPct={bufferedPct}
        playedPct={playedPct}
        onSeek={seek}
        onSkip={skip}
        fmt={fmt}
      />

      <AudioControls
        playing={playing}
        rate={rate}
        onToggle={toggle}
        onSlower={slower}
        onFaster={faster}
      />

      <audio
        ref={audioRef}
        playsInline
      />
    </div>
  );
};

export default TownLITAudioPlayer;